import React, { useState, useEffect, useCallback, useRef } from 'react';
import './IrrigationControl.css';
import useIrrigationStream from '../../hooks/useIrrigationStream';
import { useNotifications } from '../../contexts/NotificationContext';

const API = process.env.REACT_APP_API_BASE_URL || 'http://169.58.21.41/api';

function formatDuration(s) {
    if (!s && s !== 0) return '—';
    if (s < 60) return `${s}s`;
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return sec > 0 ? `${m}d ${sec}s` : `${m}d`;
}

function formatTime(iso) {
    if (!iso) return '—';
    const d = new Date(iso);
    return d.toLocaleString('tr-TR', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

export default function IrrigationControl({ field, isAdmin, onIrrigationChange, language }) {
    const isEN = language === 'en';
    const { addNotification } = useNotifications();

    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(false);
    const [modeLoading, setModeLoading] = useState(false);
    const [elapsed, setElapsed] = useState(null);
    const [showLogs, setShowLogs] = useState(false);
    const token = localStorage.getItem('auth_token');
    const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

    const prevRelayRef = useRef(null);

    const applyStatus = useCallback((data) => {
        setStatus(prev => ({ ...prev, ...data }));
        onIrrigationChange?.(data.relayOn ?? false, data);
    }, [onIrrigationChange]);

    // SSE relay event → bildirim (her cihazda tetiklenir)
    const handleRelay = useCallback((data) => {
        applyStatus(data);
        if (data.relayOn !== undefined && data.relayOn !== prevRelayRef.current) {
            if (prevRelayRef.current !== null) {
                addNotification({
                    type: 'irrigation',
                    text: data.relayOn
                        ? (isEN ? `Irrigation started — ${field?.name || 'field'}` : `Sulama başladı — ${field?.name || 'Tarla'}`)
                        : (isEN ? `Irrigation stopped — ${field?.name || 'field'}` : `Sulama durdu — ${field?.name || 'Tarla'}`),
                });
            }
            prevRelayRef.current = data.relayOn;
        }
    }, [applyStatus, addNotification, field?.name, isEN]);

    const fetchStatus = useCallback(async () => {
        if (!field?.id) return;
        try {
            const r = await fetch(`${API}/irrigation/${field.id}`, { headers: { Authorization: `Bearer ${token}` } });
            if (r.ok) {
                const data = await r.json();
                // Init prevRelayRef on first fetch without triggering notification
                if (prevRelayRef.current === null) prevRelayRef.current = data.relayOn ?? false;
                applyStatus(data);
            }
        } catch (e) {}
    }, [field?.id, token, applyStatus]);

    // SSE: instant relay/mode/dss updates
    useIrrigationStream(field?.id, {
        onInit:      (data) => { if (prevRelayRef.current === null) prevRelayRef.current = data.relayOn ?? false; applyStatus(data); },
        onRelay:     handleRelay,
        onMode:      (data) => applyStatus(data),
        onDssResult: (data) => setStatus(prev => prev ? { ...prev, lastDssResult: data } : prev),
    });

    // Fallback polling (slower, keeps logs in sync)
    useEffect(() => {
        fetchStatus();
        const t = setInterval(fetchStatus, 10000);
        return () => clearInterval(t);
    }, [fetchStatus]);

    useEffect(() => {
        if (!status?.relayOn || !status?.startedAt) { setElapsed(null); return; }
        const tick = () => setElapsed(Math.round((Date.now() - new Date(status.startedAt)) / 1000));
        tick();
        const t = setInterval(tick, 1000);
        return () => clearInterval(t);
    }, [status?.relayOn, status?.startedAt]);

    const start = async () => {
        setLoading(true);
        await fetch(`${API}/irrigation/${field.id}/start`, {
            method: 'POST', headers, body: JSON.stringify({ triggeredBy: 'manual' })
        });
        await fetchStatus();
        setLoading(false);
    };

    const stop = async () => {
        setLoading(true);
        await fetch(`${API}/irrigation/${field.id}/stop`, {
            method: 'POST', headers, body: JSON.stringify({ triggeredBy: 'manual' })
        });
        await fetchStatus();
        setLoading(false);
    };

    // Switch to AUTO: just set mode, scheduler takes over
    const enableAuto = async () => {
        setModeLoading(true);
        await fetch(`${API}/irrigation/${field.id}/mode`, {
            method: 'POST', headers, body: JSON.stringify({ mode: 'AUTO' })
        });
        await fetchStatus();
        setModeLoading(false);
    };

    // Switch to MANUAL: set mode + stop relay if auto was running it
    const enableManual = async () => {
        setModeLoading(true);
        await fetch(`${API}/irrigation/${field.id}/mode`, {
            method: 'POST', headers, body: JSON.stringify({ mode: 'MANUAL' })
        });
        // If relay was auto-started, stop it
        if (status?.relayOn && status?.startedBy === 'auto') {
            await fetch(`${API}/irrigation/${field.id}/stop`, {
                method: 'POST', headers, body: JSON.stringify({ triggeredBy: 'manual' })
            });
        }
        await fetchStatus();
        setModeLoading(false);
    };

    if (!field) return (
        <div className="irr-ctrl irr-ctrl-empty">
            {isEN ? 'Select a field' : 'Tarla seçin'}
        </div>
    );

    const isOn   = status?.relayOn;
    const isAuto = status?.mode === 'AUTO';
    const logs   = status?.logs || [];
    const recentLogs = [...logs].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)).slice(0, 10);

    return (
        <div className={`irr-ctrl ${isAuto ? 'irr-ctrl-auto' : ''} ${isOn ? 'irr-ctrl-on' : ''}`}>

            {/* ── Mode toggle ── */}
            <div className="irr-mode-row">
                <button
                    className={`irr-mode-btn ${!isAuto ? 'irr-mode-manual-active' : ''}`}
                    onClick={enableManual}
                    disabled={modeLoading || !isAuto}
                >
                    {isEN ? 'Manual' : 'Manuel'}
                </button>
                <button
                    className={`irr-mode-btn ${isAuto ? 'irr-mode-auto-active' : ''}`}
                    onClick={enableAuto}
                    disabled={modeLoading || isAuto}
                >
                    {isEN ? 'Automatic' : 'Otomatik'}
                    {isAuto
                        ? <span className="irr-mode-badge irr-mode-badge-on">{isEN ? 'Active' : 'Aktif'}</span>
                        : <span className="irr-mode-badge irr-mode-badge-off">{isEN ? 'Passive' : 'Pasif'}</span>
                    }
                </button>
            </div>

            {/* ── AUTO active banner ── */}
            {isAuto && (
                <div className={`irr-auto-banner ${isOn ? 'irr-auto-banner-irrigating' : ''}`}>
                    <span className="irr-auto-pulse" />
                    <div className="irr-auto-banner-text">
                        <span className="irr-auto-banner-title">
                            {isOn
                                ? (isEN ? 'Auto irrigation active' : 'Otomatik sulama aktif')
                                : (isEN ? 'Auto mode on — monitoring' : 'Otomatik mod açık — izleniyor')}
                        </span>
                        <span className="irr-auto-banner-sub">
                            {isEN ? 'DSS checks every 5 min' : 'DSS her 5 dk kontrol eder'}
                        </span>
                    </div>
                    {isOn && elapsed != null && (
                        <span className="irr-auto-timer">
                            {elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}d ${elapsed % 60}s`}
                        </span>
                    )}
                </div>
            )}

            {/* ── MANUAL status strip ── */}
            {!isAuto && (
                <div className="irr-ctrl-status">
                    <span className={`irr-ctrl-dot ${isOn ? 'on' : ''}`} />
                    <span className="irr-ctrl-label">
                        {isOn
                            ? (isEN ? 'IRRIGATING' : 'SULAMA AKTİF')
                            : (isEN ? 'Relay off' : 'Röle kapalı')}
                    </span>
                    {isOn && elapsed != null && (
                        <span className="irr-ctrl-elapsed">
                            {elapsed < 60 ? `${elapsed}s` : `${Math.floor(elapsed / 60)}d ${elapsed % 60}s`}
                        </span>
                    )}
                </div>
            )}

            {/* ── Action buttons ── */}
            <div className="irr-ctrl-btns">
                {!isAuto && (
                    !isOn
                        ? <button className="irr-cb irr-cb-manual" onClick={start} disabled={loading}>
                            {isEN ? 'Start Irrigation' : 'Sulamayı Başlat'}
                          </button>
                        : <button className="irr-cb irr-cb-stop" onClick={stop} disabled={loading}>
                            {isEN ? 'Stop' : 'Durdur'}
                          </button>
                )}

                {isAuto && (
                    <button
                        className="irr-cb irr-cb-auto-off"
                        onClick={enableManual}
                        disabled={modeLoading || loading}
                    >
                        {isEN ? 'Turn Off Auto Mode' : 'Otomatik Sulamayı Kapat'}
                    </button>
                )}
            </div>

            {/* ── Logs toggle ── */}
            <button className="irr-logs-toggle" onClick={() => setShowLogs(v => !v)}>
                {isEN ? 'Irrigation Logs' : 'Sulama Kayıtları'}
                {logs.length > 0 && <span className="irr-logs-badge">{logs.length}</span>}
                <span className="irr-logs-chevron">{showLogs ? '▲' : '▼'}</span>
            </button>

            {showLogs && (
                <div className="irr-logs-panel">
                    {recentLogs.length === 0 ? (
                        <div className="irr-logs-empty">{isEN ? 'No records yet' : 'Henüz kayıt yok'}</div>
                    ) : (
                        recentLogs.map(log => (
                            <div key={log.id} className={`irr-log-row ${log.action === 'START' ? 'log-start' : 'log-stop'}`}>
                                <div className="irr-log-header">
                                    <span className={`irr-log-action ${log.action === 'START' ? 'action-start' : 'action-stop'}`}>
                                        {log.action === 'START' ? '▶' : '⏹'}{' '}
                                        {log.action === 'START' ? (isEN ? 'START' : 'BAŞLAT') : (isEN ? 'STOP' : 'DURDUR')}
                                    </span>
                                    <span className="irr-log-type">
                                        {log.triggeredBy === 'manual' ? (isEN ? 'manual' : 'manuel') : (isEN ? 'auto' : 'otomatik')}
                                    </span>
                                </div>
                                <div className="irr-log-meta">
                                    <span className="irr-log-time">{formatTime(log.createdAt)}</span>
                                    {log.durationSeconds != null && (
                                        <span className="irr-log-dur">⏱ {formatDuration(log.durationSeconds)}</span>
                                    )}
                                </div>
                                {log.note && <div className="irr-log-note">{log.note}</div>}
                            </div>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}
